import { L10nCopilot } from 'helpers/l10n/L10nCopilot';
import type { IActiveCopilotRun } from 'helpers/copilotStreamClient';

import { MESSAGE_AUTHOR_TYPES } from 'constants/copilot';

import type { IChatMessage } from 'types/copilot';

const getOptimisticCancelledMessage = (runUuid: string): IChatMessage =>
  ({
    uuid: `${runUuid}:optimistic-cancelled`,
    author: MESSAGE_AUTHOR_TYPES.COPILOT,
    message: L10nCopilot.cancelledRunMessage,
    status: 'cancelled',
    copilotRunUuid: runUuid,
    dataJson: {
      responseStatus: 'cancelled',
      streamRunUuid: runUuid
    }
  }) as IChatMessage;

const isUserMessage = (message: IChatMessage): boolean =>
  message.author == null || message.author === MESSAGE_AUTHOR_TYPES.USER;

const isCancelledMessage = (message: IChatMessage): boolean =>
  message.status === 'cancelled' ||
  message.dataJson?.responseStatus === 'cancelled';

const belongsToRun = (
  message: IChatMessage,
  activeRun: IActiveCopilotRun
): boolean =>
  message.copilotRunUuid === activeRun.runUuid ||
  Boolean(
    activeRun.optimisticUserMessageClientRequestId &&
      message.clientRequestId ===
        activeRun.optimisticUserMessageClientRequestId
  );

export const completeCopilotCancellationMessages = (
  existingMessages: IChatMessage[],
  returnedMessages: IChatMessage[] | undefined,
  activeRun: IActiveCopilotRun
): IChatMessage[] => {
  const completeMessages = [...(returnedMessages ?? [])];

  if (!completeMessages.some(isUserMessage)) {
    const optimisticUserMessage = existingMessages.find(
      message => isUserMessage(message) && belongsToRun(message, activeRun)
    );
    if (optimisticUserMessage) {
      completeMessages.unshift(optimisticUserMessage);
    }
  }

  if (!completeMessages.some(isCancelledMessage)) {
    completeMessages.push(getOptimisticCancelledMessage(activeRun.runUuid));
  }

  return completeMessages;
};

export const getCopilotMessagesWithOptimisticCancellation = (
  messages: IChatMessage[],
  activeRun?: IActiveCopilotRun
): IChatMessage[] => {
  if (!activeRun?.isCancelPending) {
    return messages;
  }

  const messagesWithoutPartialAssistant = messages.filter(message => {
    if (isUserMessage(message)) {
      return true;
    }

    return (
      message.copilotRunUuid !== activeRun.runUuid &&
      (!activeRun.assistantMessageUuid ||
        message.uuid !== activeRun.assistantMessageUuid)
    );
  });

  return [
    ...messagesWithoutPartialAssistant,
    getOptimisticCancelledMessage(activeRun.runUuid)
  ];
};
